import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "listbox"]
  static values = {
    url: String,
    minLength: { type: Number, default: 3 },
    debounceMs: { type: Number, default: 250 },
    // Off by default: an address field mid-property-form shouldn't submit the whole
    // form just because a suggestion was picked. Opt in per usage (e.g. a standalone
    // search-and-filter box) via data-pro-address-autocomplete-auto-submit-value="true".
    autoSubmit: { type: Boolean, default: false },
  }

  connect() {
    this.activeIndex = -1
    this.results = []
    this.debounceTimer = null
    this.abortController = null
    this.suppressNextInput = false
    this.listboxId = `${this.inputTarget.id}-listbox`

    this.inputTarget.setAttribute("role", "combobox")
    this.inputTarget.setAttribute("aria-autocomplete", "list")
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.setAttribute("aria-controls", this.listboxId)
    this.listboxTarget.id = this.listboxId
    this.listboxTarget.setAttribute("role", "listbox")

    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    this.clearDebounce()
    this.abortFetch()
  }

  onInput() {
    // selectIndex() sets inputTarget.value then dispatches a bubbling "input" event so
    // other listeners (form dirty-tracking, etc.) see the pick — but that event also
    // re-triggers this same handler via the input's own data-action, which would
    // immediately re-fetch and reopen the listbox with the just-picked value. Skip once.
    if (this.suppressNextInput) {
      this.suppressNextInput = false
      return
    }

    this.clearDebounce()
    this.debounceTimer = window.setTimeout(() => this.fetchResults(), this.debounceMsValue)
  }

  onFocus() {
    if (this.inputTarget.value.trim().length >= this.minLengthValue && this.results.length > 0) {
      this.openListbox()
    }
  }

  onKeydown(event) {
    if (!this.isOpen()) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.moveActive(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.moveActive(-1)
        break
      case "Enter":
        if (this.activeIndex >= 0) {
          event.preventDefault()
          this.selectActive()
        }
        break
      case "Escape":
        event.preventDefault()
        this.closeListbox()
        break
      case "Tab":
        this.closeListbox()
        break
    }
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.closeListbox()
    }
  }

  async fetchResults() {
    const query = this.inputTarget.value.trim()
    if (query.length < this.minLengthValue) {
      this.clearResults()
      return
    }

    this.abortFetch()
    this.abortController = new AbortController()

    try {
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set("q", query)

      const response = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        signal: this.abortController.signal,
      })

      if (!response.ok) throw new Error("fetch failed")

      const data = await response.json()
      this.results = data.results || []
      this.activeIndex = -1
      this.renderResults()
    } catch (error) {
      if (error.name === "AbortError") return
      this.clearResults()
    }
  }

  renderResults() {
    this.listboxTarget.innerHTML = ""

    if (this.results.length === 0) {
      this.closeListbox()
      return
    }

    this.results.forEach((result, index) => {
      const option = document.createElement("li")
      option.id = `${this.listboxId}-option-${index}`
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", "false")
      option.className = "cursor-pointer rounded-lg px-3 py-2.5 text-sm text-ink touch-manipulation aria-selected:bg-primary/5 hover:bg-primary/5"
      option.dataset.index = String(index)
      option.textContent = result.label
      option.addEventListener("mousedown", (event) => event.preventDefault())
      option.addEventListener("click", () => this.selectIndex(index))
      this.listboxTarget.append(option)
    })

    this.openListbox()
  }

  moveActive(delta) {
    const count = this.results.length
    if (count === 0) return

    let next = this.activeIndex + delta
    if (next < 0) next = count - 1
    if (next >= count) next = 0

    this.setActiveIndex(next)
  }

  setActiveIndex(index) {
    this.activeIndex = index
    const options = this.listboxTarget.querySelectorAll('[role="option"]')

    options.forEach((option, i) => {
      const selected = i === index
      option.setAttribute("aria-selected", selected ? "true" : "false")
      if (selected) option.scrollIntoView({ block: "nearest" })
    })

    const active = options[index]
    this.inputTarget.setAttribute("aria-activedescendant", active ? active.id : "")
  }

  selectActive() {
    if (this.activeIndex >= 0) this.selectIndex(this.activeIndex)
  }

  selectIndex(index) {
    const result = this.results[index]
    if (!result?.label) return

    this.inputTarget.value = result.label
    this.suppressNextInput = true
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.closeListbox()
    this.clearResults()

    if (this.autoSubmitValue) {
      this.element.closest("form")?.requestSubmit()
    }
  }

  isOpen() {
    return !this.listboxTarget.hidden
  }

  openListbox() {
    this.listboxTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  closeListbox() {
    this.listboxTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  clearResults() {
    this.results = []
    this.listboxTarget.innerHTML = ""
    this.closeListbox()
  }

  clearDebounce() {
    if (this.debounceTimer) {
      window.clearTimeout(this.debounceTimer)
      this.debounceTimer = null
    }
  }

  abortFetch() {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = null
    }
  }
}
